import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { U340xKpiSeriesPoint, U340xKpiTileStatus } from '../types/u340xKpiTypes';
import type {
  U340xKpiLogicalUnitCode,
  U340xKpiMonthDetailRow,
  U340xKpiMonthDetailTotals,
  U340xKpiStandaloneInitLU,
} from '../types/u340xKpiLuTypes';
import {
  u340xBuildMetricConfig,
  u340xDetailColumnsForCode,
  u340xSeriesStatusFromLength,
} from '../utils/u340xKpiLuConfig';
import { NS } from '../u340xKpiNs';
import { useU340xKpiStandaloneHandlers } from '../hooks/useU340xKpiStandaloneHandlers';
import U340xKpiStandaloneScreen from './U340xKpiStandaloneScreen';
import type { U340xKpiMonthDetailTableStatus } from './U340xKpiMonthDetailTable';

type LuHandleError = (
  t: (key: string, options?: Record<string, unknown>) => string,
  nameSpace: string,
  err: unknown,
  errorResult: import('../../u2000Core/axios/U2000HandleApiError').U2000ApiErrorResult,
) => import('../../u2000Core/axios/U2000HandleApiError').U2000ApiErrorResult;

export interface U340xKpiStandaloneContainerProps {
  logicalUnitCode: U340xKpiLogicalUnitCode;
  nameSpace: string;
  initLU: U340xKpiStandaloneInitLU;
  luHandleAppiError: LuHandleError;
}

function U340xKpiStandaloneContainer({
  logicalUnitCode,
  nameSpace,
  initLU,
  luHandleAppiError,
}: U340xKpiStandaloneContainerProps) {
  const { t, i18n } = useTranslation([nameSpace, NS]);
  const { fetchStandaloneSeries, fetchMonthDetail, upsertMonthsStandalone, handleError } =
    useU340xKpiStandaloneHandlers({
      logicalUnitCode,
      nameSpace,
      luHandleAppiError,
    });

  const [months, setMonths] = useState(
    initLU.monthPreference.monthsStandaloneEffective,
  );
  const [anchorMonth, setAnchorMonth] = useState(initLU.defaultMonth);
  const [series, setSeries] = useState<U340xKpiSeriesPoint[]>([]);
  const [seriesStatus, setSeriesStatus] = useState<U340xKpiTileStatus>('loading');
  const [seriesErrorMessage, setSeriesErrorMessage] = useState<string | undefined>();

  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [detailRows, setDetailRows] = useState<U340xKpiMonthDetailRow[]>([]);
  const [detailTotals, setDetailTotals] = useState<U340xKpiMonthDetailTotals | null>(null);
  const [detailStatus, setDetailStatus] = useState<U340xKpiMonthDetailTableStatus>('idle');
  const [detailErrorMessage, setDetailErrorMessage] = useState<string | undefined>();

  const isEn = i18n.language?.toLowerCase().startsWith('en');
  const title = isEn ? initLU.tile.labelEn : initLU.tile.labelFr;

  const metrics = useMemo(
    () => u340xBuildMetricConfig(logicalUnitCode, (key) => t(key, { ns: NS })),
    [logicalUnitCode, t],
  );
  const detailColumns = useMemo(
    () => u340xDetailColumnsForCode(logicalUnitCode),
    [logicalUnitCode],
  );

  const resetDetail = () => {
    setSelectedMonth(null);
    setDetailStatus('idle');
    setDetailRows([]);
    setDetailTotals(null);
  };

  const loadSeries = useCallback(
    async (monthsCount: number, anchor: string) => {
      setSeriesStatus('loading');
      setSeriesErrorMessage(undefined);
      try {
        const result = await fetchStandaloneSeries(monthsCount, anchor);
        const nextSeries = (result?.series ?? []) as U340xKpiSeriesPoint[];
        setSeries(nextSeries);
        setSeriesStatus(u340xSeriesStatusFromLength(nextSeries.length));
        if (result?.anchorMonth) {
          setAnchorMonth(result.anchorMonth);
        }
      } catch (error: unknown) {
        handleError(error);
        setSeries([]);
        setSeriesStatus('error');
        setSeriesErrorMessage(t('errSeriesLoad', { ns: nameSpace }));
      }
    },
    [fetchStandaloneSeries, handleError, nameSpace, t],
  );

  useEffect(() => {
    void loadSeries(months, anchorMonth);
    // Chargement initial + changements explicites via handlers ci-dessous
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMonthsChange = async (nextMonths: number) => {
    const saved = await upsertMonthsStandalone(nextMonths);
    if (!saved) return;
    const effective = saved.monthsStandaloneEffective ?? nextMonths;
    setMonths(effective);
    resetDetail();
    await loadSeries(effective, anchorMonth);
  };

  const handleAnchorMonthChange = async (nextAnchor: string) => {
    setAnchorMonth(nextAnchor);
    resetDetail();
    await loadSeries(months, nextAnchor);
  };

  const handleMonthClick = async (month: string) => {
    setSelectedMonth(month);
    setDetailStatus('loading');
    setDetailErrorMessage(undefined);
    setDetailTotals(null);
    try {
      const result = await fetchMonthDetail(month);
      const rows = result?.rows ?? [];
      setDetailRows(rows);
      setDetailTotals(result?.totals ?? null);
      setDetailStatus(rows.length === 0 ? 'empty' : 'ready');
    } catch (error: unknown) {
      handleError(error);
      setDetailRows([]);
      setDetailTotals(null);
      setDetailStatus('error');
      setDetailErrorMessage(t('errDetailLoad', { ns: nameSpace }));
    }
  };

  return (
    <U340xKpiStandaloneScreen
      title={title}
      series={series}
      primaryMetric={metrics.primaryMetric}
      secondaryMetric={metrics.secondaryMetric}
      primaryStackMetric={metrics.primaryStackMetric}
      secondaryExtraMetric={metrics.secondaryExtraMetric}
      secondaryTecMetric={metrics.secondaryTecMetric}
      secondaryTecExtraMetric={metrics.secondaryTecExtraMetric}
      months={months}
      anchorMonth={anchorMonth}
      seriesStatus={seriesStatus}
      seriesErrorMessage={seriesErrorMessage}
      selectedMonth={selectedMonth}
      detailColumns={detailColumns}
      detailRows={detailRows}
      detailStatus={detailStatus}
      detailErrorMessage={detailErrorMessage}
      detailTotals={detailTotals}
      onMonthsChange={(next) => {
        void handleMonthsChange(next);
      }}
      onAnchorMonthChange={(next) => {
        void handleAnchorMonthChange(next);
      }}
      onMonthClick={(month) => {
        void handleMonthClick(month);
      }}
    />
  );
}

export default U340xKpiStandaloneContainer;
